/**
 * AGC: AirdropManagerV2 Hook
 * AGC: Handles airdrop claims via AirdropManagerV2 contract with rounds support
 * AGC: Checks if user has claimed in current round (allows re-claiming in new rounds)
 */
import { useAccount, useReadContract, useWriteContract, useWaitForTransactionReceipt } from 'wagmi'
import { parseEther } from 'viem'
import { useMiniKit } from '@coinbase/onchainkit/minikit'
import { getDeviceId } from '../utils/deviceFingerprint'
import { useFeatureFlags } from './useFeatureFlags'

// AGC: AirdropManagerV2 contract ABI
const AIRDROP_MANAGER_V2_ABI = [
  {
    name: 'currentRound',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'hasClaimedTokens',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'wallet', type: 'address' }],
    outputs: [{ name: '', type: 'bool' }]
  },
  {
    name: 'hasClaimedNFTs',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'wallet', type: 'address' }],
    outputs: [{ name: '', type: 'bool' }]
  },
  {
    name: 'claimTokenAirdrop',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'profileIdHash', type: 'bytes32' },
      { name: 'nonce', type: 'uint256' },
      { name: 'expiry', type: 'uint256' },
      { name: 'signature', type: 'bytes' }
    ],
    outputs: []
  },
  {
    name: 'claimNFTAirdrop',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'profileIdHash', type: 'bytes32' },
      { name: 'nonce', type: 'uint256' },
      { name: 'expiry', type: 'uint256' },
      { name: 'signature', type: 'bytes' }
    ],
    outputs: []
  },
  {
    name: 'tokenAirdropAmount',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'paused',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'bool' }]
  }
] as const

interface ClaimVoucher {
  profileIdHash: string
  wallet: string
  nonce: number
  expiry: number
  signature: string
}

interface ClaimResponse {
  success: boolean
  voucher: ClaimVoucher
}

/**
 * AGC: Hook to check if user can claim airdrop in current round
 */
export function useCanClaimAirdropV2(address?: `0x${string}`) {
  const airdropManagerV2Address = (import.meta.env.VITE_AIRDROP_MANAGER_V2_ADDRESS || '0x7AF0F520812d0Ad7bc05217E31B8404a66108143') as `0x${string}`

  const { data: hasClaimed, isLoading, error, refetch } = useReadContract({
    address: airdropManagerV2Address,
    abi: AIRDROP_MANAGER_V2_ABI,
    functionName: 'hasClaimedTokens',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && airdropManagerV2Address !== '0x0000000000000000000000000000000000000000',
    },
  })

  const { data: isPaused } = useReadContract({
    address: airdropManagerV2Address,
    abi: AIRDROP_MANAGER_V2_ABI,
    functionName: 'paused',
    query: {
      enabled: !!airdropManagerV2Address && airdropManagerV2Address !== '0x0000000000000000000000000000000000000000',
    },
  })

  return {
    canClaim: address && !hasClaimed && !isPaused,
    hasClaimed: Boolean(hasClaimed),
    isPaused: Boolean(isPaused),
    isLoading,
    error: error as Error | null,
    refetch
  }
}

/**
 * AGC: Hook for claiming token airdrop via AirdropManagerV2
 */
export function useClaimAirdropV2() {
  const { address, isConnected } = useAccount()
  // AGC: Conditionally use MiniKit only in Base App context (v5.0.1 pattern)
  const { isBaseAppContext } = useFeatureFlags()
  const miniKit = isBaseAppContext ? useMiniKit() : { context: null }
  const { context } = miniKit
  const { writeContractAsync, data: hash, isPending, error } = useWriteContract()
  
  // AGC: Wait for transaction receipt
  const { data: receipt, isLoading: isConfirming } = useWaitForTransactionReceipt({
    hash,
  })

  const fid = context?.user?.fid
  const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001'
  const airdropManagerV2Address = (import.meta.env.VITE_AIRDROP_MANAGER_V2_ADDRESS || '0x7AF0F520812d0Ad7bc05217E31B8404a66108143') as `0x${string}`

  /**
   * AGC: Claim token airdrop via AirdropManagerV2 with backend voucher
   */
  const claimTokenAirdrop = async () => {
    if (!fid) {
      throw new Error('Base FID not found. Please open in Base App.')
    }

    if (!address) {
      throw new Error('Wallet not connected')
    }

    if (!airdropManagerV2Address) {
      throw new Error('AirdropManagerV2 contract address not configured')
    }

    // AGC: Get device fingerprint
    const deviceId = getDeviceId()

    // AGC: Request signed voucher from backend
    // AGC: TODO - Backend needs to support AirdropManagerV2
    // AGC: For now, this will fail until backend is updated
    const response = await fetch(`${backendUrl}/api/airdrop/claim`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-FID': String(fid),
      },
      body: JSON.stringify({
        profileId: fid,
        walletAddress: address,
        deviceId,
        version: 'v2' // AGC: Indicate we want AirdropManagerV2 voucher
      }),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to claim airdrop')
    }

    const data: ClaimResponse = await response.json()
    const { voucher } = data

    // AGC: Call AirdropManagerV2 contract with signed voucher
    console.log('AGC: Calling AirdropManagerV2.claimTokenAirdrop()...')
    const txHash = await writeContractAsync({
      address: airdropManagerV2Address,
      abi: AIRDROP_MANAGER_V2_ABI,
      functionName: 'claimTokenAirdrop',
      args: [
        voucher.profileIdHash as `0x${string}`,
        BigInt(voucher.nonce),
        BigInt(voucher.expiry),
        voucher.signature as `0x${string}`,
      ],
    })

    console.log('AGC: Airdrop claim transaction submitted:', txHash)
    return txHash
  }

  /**
   * AGC: Claim NFT airdrop via AirdropManagerV2 with backend voucher
   */
  const claimNFTAirdrop = async () => {
    if (!fid) {
      throw new Error('Base FID not found. Please open in Base App.')
    }

    if (!address) {
      throw new Error('Wallet not connected')
    }

    if (!airdropManagerV2Address) {
      throw new Error('AirdropManagerV2 contract address not configured')
    }

    // AGC: Get device fingerprint
    const deviceId = getDeviceId()

    // AGC: Request signed voucher from backend for NFT claim
    const response = await fetch(`${backendUrl}/api/airdrop/claim-nft`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-FID': String(fid),
      },
      body: JSON.stringify({
        profileId: fid,
        walletAddress: address,
        deviceId,
        version: 'v2' // AGC: Indicate we want AirdropManagerV2 voucher
      }),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to claim NFT airdrop')
    }

    const data: ClaimResponse = await response.json()
    const { voucher } = data

    // AGC: Call AirdropManagerV2 contract with signed voucher
    console.log('AGC: Calling AirdropManagerV2.claimNFTAirdrop()...')
    const txHash = await writeContractAsync({
      address: airdropManagerV2Address,
      abi: AIRDROP_MANAGER_V2_ABI,
      functionName: 'claimNFTAirdrop',
      args: [
        voucher.profileIdHash as `0x${string}`,
        BigInt(voucher.nonce),
        BigInt(voucher.expiry),
        voucher.signature as `0x${string}`,
      ],
    })

    console.log('AGC: NFT airdrop claim transaction submitted:', txHash)
    return txHash
  }

  /**
   * AGC: Claim both token and NFT airdrops (two separate transactions)
   */
  const claimFullAirdrop = async () => {
    // AGC: First claim tokens
    const tokenTxHash = await claimTokenAirdrop()
    console.log('AGC: Token airdrop claimed, waiting for confirmation...')
    
    // AGC: Wait for token claim to be confirmed
    // Note: This is a simplified version - in production you might want to wait for receipt
    await new Promise(resolve => setTimeout(resolve, 3000))
    
    // AGC: Then claim NFTs
    const nftTxHash = await claimNFTAirdrop()
    console.log('AGC: NFT airdrop claimed')
    
    return { tokenTxHash, nftTxHash }
  }

  /**
   * AGC: Claim NFTs only (for users who already claimed tokens but missed NFTs)
   */
  const claimNFTsOnly = async () => {
    console.log('🎁 [AirdropV2] Claiming NFTs only...')
    const nftTxHash = await claimNFTAirdrop()
    console.log('🎁 [AirdropV2] NFT airdrop claimed')
    return { nftTxHash }
  }

  return {
    claimTokenAirdrop,
    claimNFTAirdrop,
    claimNFTsOnly, // AGC: Convenience function for users who missed NFTs
    claimFullAirdrop,
    hash,
    receipt,
    isPending,
    isConfirming,
    isSuccess: receipt?.status === 'success',
    error,
    canClaim: !!fid && isConnected && !!airdropManagerV2Address,
  }
}

